﻿import {DragEvent, HTMLAttributes, KeyboardEvent, MouseEvent, useCallback, useMemo, useState} from "react";

/** Class names applied to rows during a drag, overridable per call site. */
export type DragReorderClassNames = {
	/** Applied to the row being dragged (the source). Defaults to `"row--dragging"`. */
	dragging?: string;
	/**
	 * Applied to the row currently hovered as the drop position. Defaults to `"row--drop-target"`.
	 * A dropped row lands *after* the target when moving down and *before* it when moving up, so
	 * style the indicator edge from the sibling order — `.dragging ~ .dropTarget` is the
	 * moving-down case and gets the bottom edge; the bare class gets the top edge.
	 */
	dropTarget?: string;
};

const defaultClassNames: Required<DragReorderClassNames> = {
	dragging: "row--dragging",
	dropTarget: "row--drop-target"
};

export type DragReorderOptions = {
	/**
	 * When `true`, rows are not draggable and no drag handlers/feedback are
	 * attached. Use this whenever the rendered index no longer maps 1:1 to the
	 * reordered array — e.g. while a table filter narrows the visible rows — so a
	 * drop can't silently move the wrong item.
	 */
	disabled?: boolean;
	/**
	 * When `true`, the drag is initiated from a dedicated handle inside a cell
	 * rather than from the whole row. `draggable`/`onDragStart` move off
	 * {@link DragReorderProps.rowProps} and onto {@link DragReorderProps.handleProps},
	 * which you spread onto the handle element (e.g. `TableReorderCell`); the
	 * row keeps the drop handling and visual feedback and is still what gets moved.
	 * Defaults to `false` (the whole row is the handle).
	 */
	handle?: boolean;
	/** CSS feedback classes, defaulting to `row--dragging` / `row--drop-target`. */
	classNames?: DragReorderClassNames;
	/**
	 * Selector of the row element the handle lives in, used as the drag image so the
	 * whole row is what appears to move. Defaults to `"tr"` (a {@link Table} row);
	 * pass the row class when the list isn't a table (e.g. `".widget-repeater__row"`).
	 */
	rowSelector?: string;
};

/**
 * The builders returned by {@link useDragReorder}.
 *
 * - `rowProps` is shaped exactly like {@link Table}'s `rowProps` and carries the
 *   drop handling plus visual feedback (and, unless `handle` is set, the drag
 *   initiation): `<Table rowProps={rowProps} … />`.
 * - `handleProps` is empty unless `handle` is enabled; when it is, it carries the
 *   `draggable`/`onDragStart` that start the drag plus the keyboard equivalent, for
 *   spreading onto an in-row handle keyed by the row index: `<span {...handleProps(index)} />`.
 */
export type DragReorderProps<T> = {
	rowProps: (row: T, index: number) => HTMLAttributes<HTMLElement>;
	handleProps: (index: number) => HTMLAttributes<HTMLElement>;
};

/**
 * Native HTML5 drag-and-drop reordering for a list of rows. Tracks the dragged
 * source row and the hovered drop position internally and returns builders for
 * the row and (optionally) an in-row drag handle.
 *
 * `onReorder(from, to)` performs the actual move and is the only required input;
 * pair it with `useReorderHandler` to reorder an array inside an MVVM
 * context (`useDragReorder(useReorderHandler(context, "columns"))`). It must ignore a `to`
 * past the end of the list: the keyboard handle asks for `index + 1` on the last row (it
 * drops `index - 1` on the first itself, being the only bound it can know).
 *
 * By default the whole row is the drag handle and only `rowProps` is needed.
 * Pass `handle: true` to instead start the drag from an element inside the row:
 * spread `handleProps(index)` onto that element (see `TableReorderCell`)
 * while still passing `rowProps` to the row — the row remains the drop target,
 * shows the drag feedback, and is what gets reordered. Pass `disabled` to
 * suppress dragging when the rendered order doesn't match the reordered array
 * (e.g. an active filter).
 */
export function useDragReorder<T>(
	onReorder: (from: number, to: number) => void,
	options?: DragReorderOptions
): DragReorderProps<T> {
	const {disabled = false, handle = false, classNames, rowSelector = "tr"} = options ?? {};
	const {dragging, dropTarget} = {...defaultClassNames, ...classNames};

	const [dragIndex, setDragIndex] = useState<number | null>(null);
	const [overIndex, setOverIndex] = useState<number | null>(null);

	const reset = useCallback(() => {
		setDragIndex(null);
		setOverIndex(null);
	}, []);

	const startDrag = useCallback((index: number, event: DragEvent) => {
		setDragIndex(index);
		event.dataTransfer.effectAllowed = "move";
	}, []);

	// Drop handling and visual feedback always live on the row; only the drag
	// *initiation* moves to the handle when `handle` is set, so this is shared.
	const rowDropProps = useCallback((index: number): HTMLAttributes<HTMLElement> => ({
		onDragOver: (event: DragEvent<HTMLElement>) => {
			if (dragIndex === null) {
				return;
			}
			event.preventDefault();
			event.dataTransfer.dropEffect = "move";
			if (overIndex !== index) {
				setOverIndex(index);
			}
		},
		// dragleave also fires when crossing into a child, so only clear on a real exit
		onDragLeave: (event: DragEvent<HTMLElement>) => {
			if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
				setOverIndex(current => current === index ? null : current);
			}
		},
		onDrop: (event: DragEvent<HTMLElement>) => {
			event.preventDefault();
			if (dragIndex !== null) {
				onReorder(dragIndex, index);
			}
			reset();
		},
		onDragEnd: reset,
		className: dragIndex === index
			? dragging
			: (dragIndex !== null && overIndex === index ? dropTarget : undefined)
	}), [dragIndex, overIndex, onReorder, dragging, dropTarget, reset]);

	const rowProps = useCallback((_row: T, index: number): HTMLAttributes<HTMLElement> => {
		if (disabled) {
			return {draggable: false};
		}
		const drop = rowDropProps(index);
		if (handle) {
			return drop;
		}
		return {
			...drop,
			draggable: true,
			onDragStart: (event: DragEvent<HTMLElement>) => startDrag(index, event)
		};
	}, [disabled, handle, rowDropProps, startDrag]);

	const handleProps = useCallback((index: number): HTMLAttributes<HTMLElement> => {
		if (disabled || !handle) {
			return {draggable: false, "aria-disabled": disabled || undefined};
		}
		return {
			draggable: true,
			// HTML5 drag is pointer-only, so the handle is also focusable and moves the row
			// with the arrow keys. No role: a drag handle isn't a button (nothing sensible
			// happens on Enter/Space), so name it from the call site instead.
			tabIndex: 0,
			"aria-keyshortcuts": "ArrowUp ArrowDown",
			// A pointer press must not leave the handle armed for the arrow keys: with a mouse you
			// drag, and a handle left focused after a plain click reads as a row that is somehow
			// "on". Tab focus never fires mouseup, so the keyboard path keeps its arrow keys.
			onMouseUp: (event: MouseEvent<HTMLElement>) => event.currentTarget.blur(),
			onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
				const to = event.key === "ArrowUp" ? index - 1 : event.key === "ArrowDown" ? index + 1 : null;
				if (to === null || to < 0) {
					return;
				}
				event.preventDefault();
				// only the upper bound is left for onReorder: it's the one that knows the length
				onReorder(index, to);
			},
			onDragStart: (event: DragEvent<HTMLElement>) => {
				startDrag(index, event);
				// The handle only initiates the drag; drag the whole row so the
				// feedback (and what the user sees moving) matches the reorder.
				const row = event.currentTarget.closest(rowSelector);
				if (row) {
					event.dataTransfer.setDragImage(row, 0, 0);
				}
			}
		};
	}, [disabled, handle, startDrag, onReorder, rowSelector]);

	return useMemo(() => ({rowProps, handleProps}), [rowProps, handleProps]);
}
